import { useEffect, useCallback } from "react";
import { isVitalsSubdomain, hasVitalsAIBetaFeature } from "../util/subdomain";
import { useAppSelector } from "./redux";

// Extend Window interface to include custom property for hash tracking
declare global {
  interface Window {
    __lastHash?: string;
  }
}

// Constants for timing and intervals
const HASH_CHECK_INTERVAL_MS = 200; // Check every 200ms (5 times per second)

/**
 * Hook to detect if the app is running on vitals.circadia.link subdomain
 * or localhost/172.20.10.2 (for testing) and redirect authenticated users to the vitals-ai route.
 *
 * Authentication Handling:
 * - Unauthenticated users (isAuthorized = false) are NOT redirected - they see login page
 * - Once user logs in (isAuthorized = true), they are automatically redirected to vitals-ai
 * - This prevents "No current user" errors and ensures proper login flow
 *
 * Exceptions (for authenticated users):
 * - Login pages (/login, /sso/login, /user-info) - already logged in users can access
 * - Change-facility page - users can switch facilities
 * - Settings page - users can access settings
 * - Patient profile pages - for vitals history viewing
 */
export const useVitalsSubdomainRedirect = () => {
  // Check if user is authenticated - don't redirect unauthenticated users
  const isAuthorized = useAppSelector((state) => state.auth.isAuthorized);
  const betaFeaturesAllowedList = useAppSelector(
    (state) => state.auth.betaFeaturesAllowedList
  );

  const checkAndRedirect = useCallback(() => {
    // Check if we're in vitals AI mode (subdomain OR beta feature)
    const isVitalsAIMode =
      isVitalsSubdomain() || hasVitalsAIBetaFeature(betaFeaturesAllowedList);

    if (isVitalsAIMode) {
      // Don't redirect unauthenticated users - let them access login page
      if (!isAuthorized) {
        return;
      }

      // Get current path and search params
      const currentPath = window.location.pathname;
      const currentSearch = window.location.search;
      const currentHash = window.location.hash;

      // Check if we're already on the vitals-ai route (including history page) or patient profile route
      const isAlreadyOnVitalsAI =
        currentHash === "#/vitals-ai" ||
        currentHash.includes("#/vitals-ai/history") ||
        currentPath.includes("vitals-ai");
      const isOnPatientProfile =
        currentHash.includes("#/patient-profile") ||
        currentPath.includes("patient-profile");

      // Check if we're on login/auth pages - don't redirect if user needs to login first
      const isOnLoginPage =
        currentHash.includes("#/login") || currentPath.includes("login");

      // Without this check, someone signing in via PCC can get stuck in a loop that makes them login twice. (Tested by Umer.)
      const isSSOLoginPage =
        currentHash.includes("#/sso/login") ||
        currentPath.includes("sso/login");

      const isOnUserInfoPage =
        currentHash.includes("#/user-info") ||
        currentPath.includes("user-info");

      // Check if we're on change-facility page - allow users to change facility
      const isOnChangeFacility =
        currentHash.includes("#/change-facility") ||
        currentPath.includes("change-facility");

      // Check if we're on settings page - allow users to access settings
      const isOnSettingsPage =
        currentHash.includes("#/settings") || currentPath.includes("settings");

      // Don't redirect if user is on login page - let them login first
      if (isOnLoginPage || isSSOLoginPage || isOnUserInfoPage) {
        return;
      }

      // Don't redirect if user is on change-facility page - let them change facility
      if (isOnChangeFacility) {
        return;
      }

      // Don't redirect if user is on settings page - let them access settings
      if (isOnSettingsPage) {
        return;
      }

      if (
        !isAlreadyOnVitalsAI &&
        !isOnPatientProfile &&
        !isOnLoginPage &&
        !isSSOLoginPage &&
        !isOnChangeFacility &&
        !isOnSettingsPage &&
        !isOnUserInfoPage
      ) {
        // Redirect to vitals-ai route with preserved search params
        const redirectUrl = `${window.location.origin}/#/vitals-ai${currentSearch}`;

        try {
          window.location.href = redirectUrl;
        } catch (error) {
          console.error("Failed to redirect to vitals-ai:", error);
          // Fallback: try to navigate using history API
          try {
            window.history.replaceState(null, "", redirectUrl);
            window.location.reload();
          } catch (fallbackError) {
            console.error("Fallback redirect also failed:", fallbackError);
          }
        }
      }
    }
  }, [isAuthorized, betaFeaturesAllowedList]);

  // Re-check when auth status changes (e.g., after user logs in)
  useEffect(() => {
    checkAndRedirect();
  }, [isAuthorized, checkAndRedirect]);

  useEffect(() => {
    // Check on initial mount
    checkAndRedirect();

    // Also listen for hash changes (for SPA navigation)
    const handleHashChange = () => {
      checkAndRedirect();
    };

    // Listen for popstate events (back/forward navigation)
    const handlePopState = () => {
      checkAndRedirect();
    };

    window.addEventListener("hashchange", handleHashChange);
    window.addEventListener("popstate", handlePopState);

    // Only set up polling intervals if in vitals AI mode (performance optimization)
    const isVitalsAIMode =
      isVitalsSubdomain() || hasVitalsAIBetaFeature(betaFeaturesAllowedList);
    let intervalId: NodeJS.Timeout | undefined;

    if (isVitalsAIMode) {
      // Set up an interval to check for hash changes (fallback for missed events)
      intervalId = setInterval(() => {
        const currentHash = window.location.hash;
        if (currentHash !== window.__lastHash) {
          window.__lastHash = currentHash;
          checkAndRedirect();
        }
      }, HASH_CHECK_INTERVAL_MS);

      // Initialize the last hash
      window.__lastHash = window.location.hash;
    }

    // Cleanup
    return () => {
      window.removeEventListener("hashchange", handleHashChange);
      window.removeEventListener("popstate", handlePopState);
      if (intervalId) clearInterval(intervalId);
    };
  }, [isAuthorized, betaFeaturesAllowedList]); // Re-run when auth or beta features change

  return {
    isVitalsSubdomain: isVitalsSubdomain(),
    isVitalsAIMode:
      isVitalsSubdomain() || hasVitalsAIBetaFeature(betaFeaturesAllowedList),
  };
};
